import React, { useState } from 'react'

import {
  Box,
  Button,
  ButtonBase,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  Paper,
  Typography,
} from '@mui/material'

import Data, { IProject } from '../../../api/Data'
import { DEFAULT_PROJECT_TYPE, PROJECT_TYPE_OPTIONS, ProjectType } from '../../../config/projectTypes'

const ProjectWorkflowCard = ({ project, handleChange }: IProps) => {
  const selectedType = project.projectType || DEFAULT_PROJECT_TYPE
  const [requestedType, setRequestedType] = useState<ProjectType | null>(null)
  const [duplicating, setDuplicating] = useState(false)
  const hasSubmissions = (project.clipCount || 0) > 0

  const selectWorkflow = (event: React.MouseEvent<HTMLElement>, projectType: ProjectType) => {
    const updates: Partial<IProject> = { projectType }

    if (projectType === 'video_contribution') {
      updates.maxFilesPerSubmission = 1
      updates.allowBatchUpload = false
    } else if (projectType === 'video_collection') {
      updates.allowUploadFile = true
      updates.maxFilesPerSubmission = Math.max(2, project.maxFilesPerSubmission || 2)
    } else if (projectType === 'video_capture') {
      updates.allowRecordVideo = true
      updates.numberOfQuestions = 1
      updates.questions = [project.questions?.[0] || 'Tell us about your experience']
    } else if (projectType === 'video_conversation') {
      const numberOfQuestions = Math.max(2, project.numberOfQuestions || 2)
      updates.allowRecordVideo = true
      updates.allowUploadFile = false
      updates.allowBatchUpload = false
      updates.maxFilesPerSubmission = 1
      updates.numberOfQuestions = numberOfQuestions
      updates.questions = Array.from({ length: numberOfQuestions }, (_, index) => project.questions?.[index] || '')
    } else if (projectType === 'video_compliance') {
      updates.allowRecordVideo = true
    }

    handleChange(event, { updates })
  }

  const requestWorkflow = (event: React.MouseEvent<HTMLElement>, projectType: ProjectType) => {
    if (projectType === selectedType) return

    if (hasSubmissions) {
      setRequestedType(projectType)
      return
    }

    selectWorkflow(event, projectType)
  }

  const duplicateForNewWorkflow = async () => {
    setDuplicating(true)
    const { data, error } = await Data.duplicateProject(project.slug)
    setDuplicating(false)

    if (error) {
      alert('The project could not be duplicated. Please try again.')
      return
    }

    const duplicatedProject = data?.project || data
    const duplicatedSlug = duplicatedProject?.slug
    if (!duplicatedSlug) {
      alert('The project was duplicated, but the new project could not be opened automatically.')
      return
    }

    window.location.assign(`/projects/${duplicatedSlug}/configure?tab=overview`)
  }

  return (
    <Paper sx={{ padding: 2 }}>
      <Typography variant="h5" fontWeight="bold">
        Submission Workflow
      </Typography>
      <Typography variant="body1" color="textSecondary" sx={{ marginBottom: 2 }}>
        Choose how contributors will provide their video content. Only settings relevant to this workflow will be shown
        below.
      </Typography>

      <Grid container spacing={1.5} role="radiogroup" aria-label="Submission workflow">
        {PROJECT_TYPE_OPTIONS.map((option) => {
          const selected = selectedType === option.value
          const isComingSoon = option.value === 'video_compliance'

          return (
            <Grid item xs={12} sm={6} lg key={option.value}>
              <ButtonBase
                role="radio"
                aria-checked={selected}
                aria-disabled={isComingSoon}
                disabled={isComingSoon}
                onClick={(event) => requestWorkflow(event, option.value)}
                sx={{
                  alignItems: 'stretch',
                  backgroundColor: isComingSoon ? '#f3f4f6' : 'transparent',
                  border: selected ? '2px solid #6b50ff' : '1px solid #d1d5db',
                  borderRadius: '8px',
                  boxSizing: 'border-box',
                  color: isComingSoon ? '#9ca3af' : 'inherit',
                  display: 'flex',
                  height: '100%',
                  justifyContent: 'flex-start',
                  opacity: isComingSoon ? 0.72 : 1,
                  padding: 1.5,
                  textAlign: 'left',
                  width: '100%',
                }}
              >
                <Box>
                  <Typography variant="h6" fontWeight="bold" color={selected ? 'primary' : 'textPrimary'}>
                    {option.text}
                  </Typography>
                  {isComingSoon && (
                    <Typography variant="caption" fontWeight="bold" color="textSecondary">
                      Coming soon
                    </Typography>
                  )}
                  <Typography variant="subtitle2" fontWeight="bold" sx={{ marginBottom: 0.75 }}>
                    {option.workflowName}
                  </Typography>
                  <Typography variant="body2" color="textSecondary">
                    {option.description}
                  </Typography>
                </Box>
              </ButtonBase>
            </Grid>
          )
        })}
      </Grid>

      <Dialog open={!!requestedType} onClose={() => !duplicating && setRequestedType(null)}>
        <DialogTitle>Duplicate this project to change its workflow</DialogTitle>
        <DialogContent>
          <Typography>
            This project already contains video submissions, so its workflow is locked to protect existing contributor
            data. Duplicate it to create a clean project, then select the new workflow there.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button disabled={duplicating} onClick={() => setRequestedType(null)}>
            Cancel
          </Button>
          <Button variant="contained" disabled={duplicating} onClick={duplicateForNewWorkflow}>
            {duplicating ? 'Duplicating…' : 'Duplicate project'}
          </Button>
        </DialogActions>
      </Dialog>
    </Paper>
  )
}

interface IProps {
  project: IProject
  handleChange: (event: React.SyntheticEvent, data: any) => void
}

export default ProjectWorkflowCard
